import { Box, Button, Flex, HStack, Image, Separator, Stack, Text } from "@chakra-ui/react"
import { Form, UI } from "components"
import { API } from "config"
import { toaster } from "config"
import { useState } from "react"
import OneSignal from "react-onesignal"
import { Link, useNavigate } from "react-router"
import GlobeCanvas from "../../components/GlobeCanvas"

const FEATURES = [
    "Gestão de estoque em tempo real",
    "Pedidos e entregas dos consultores",
    "Relatórios de vendas e comissões",
]

export default function Auth(){

    const navigate = useNavigate()

    const [ loading, setLoading ] = useState(false)
    const [ email, setEmail ] = useState()
    const [ password, setPassword ] = useState()

    const handleSubmit = async () => {
        setLoading(true)

        try{
            const callback = await API.post('/auth', { email, password })

            // Save token
            localStorage.setItem('@token', callback.data.plainTextToken)

            await check(callback.data.plainTextToken)

        }catch(err){
            toaster.create({ title: 'Não foi possível autenticar', description: err?.response?.data?.message, type: 'warning' })
            setLoading(false)
        }

    }

    const check = async (token) => {
        try{
            const user = await API.get('/me', { headers: {"Accept": 'application/json', "Content-Type": 'application/json', "Authorization": `Bearer ${token}`}})

            // Vínculo com OneSignal é best-effort — falha silenciosa não bloqueia o login
            OneSignal.login(String(user.data?.id)).catch(() => {})

            toaster.create({ description: `Bem vindo(a) de volta!`, type: 'success' })
            navigate('/')
            window.location.reload()
        }catch(err){
            localStorage.clear()
            toaster.create({ title: err?.response?.data?.message ?? 'Não foi possível acessar o sistema.', type: 'warning' })
            setLoading(false)
        }
    }

    return(
    <Flex h="100vh" overflow="hidden">

        {/* ── Painel esquerdo: Identidade da marca ── */}
        <Flex
            hideBelow="lg"
            flex="1"
            direction="column"
            justify="space-between"
            position="relative"
            overflow="hidden"
            style={{ background: 'linear-gradient(155deg, #070c17 0%, #0d1525 50%, #0a1628 100%)' }}
            p={12}
        >
            {/* Globe — centralizado no painel */}
            <Box
                position="absolute"
                inset={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ pointerEvents: 'none' }}
                zIndex={0}
            >
                <GlobeCanvas size={720} />
            </Box>

            {/* Gradient top */}
            <Box
                position="absolute"
                top={0} left={0} right={0} h="42%"
                style={{ background: 'linear-gradient(to bottom, rgba(7,12,23,0.93) 0%, rgba(7,12,23,0.52) 68%, transparent 100%)', pointerEvents: 'none' }}
                zIndex={1}
            />

            {/* Gradient bottom */}
            <Box
                position="absolute"
                bottom={0} left={0} right={0} h="42%"
                style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.93) 0%, rgba(15,23,42,0.52) 68%, transparent 100%)', pointerEvents: 'none' }}
                zIndex={1}
            />

            {/* Top: brand badge — mesmo padrão do consultor */}
            <HStack gap={4} zIndex={2}>
                <Box
                    p="8px"
                    borderRadius="12px"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                >
                    <Image src="/icon.png" h="30px" />
                </Box>
                <Box>
                    <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">Bonífica</Text>
                    <Box mt="4px" display="inline-flex">
                        <Box
                            px={2} py="2px"
                            borderRadius="full"
                            style={{ background: 'rgba(251,146,60,0.18)', border: '1px solid rgba(251,146,60,0.32)' }}
                        >
                            <Text color="orange.300" fontSize="xs" fontWeight="medium">Escritório de Núcleos</Text>
                        </Box>
                    </Box>
                </Box>
            </HStack>

            {/* Bottom: headline + features */}
            <Box zIndex={2}>
                <Text color="white" fontSize="2xl" fontWeight="semibold" lineHeight="1.4">
                    Gerencie seu núcleo<br />com inteligência
                </Text>
                <Text mt={2} fontSize="sm" style={{ color: 'rgba(255,255,255,0.55)' }} lineHeight="1.6">
                    Plataforma completa para vendas, estoque e relatórios do seu núcleo.
                </Text>

                <Box mt={5} display="flex" flexDirection="column" gap="8px">
                    {FEATURES.map(f => (
                        <HStack key={f} gap={2}>
                            <Box w="5px" h="5px" borderRadius="full" style={{ background: '#F7941D', flexShrink: 0 }} />
                            <Text fontSize="xs" style={{ color: 'rgba(255,255,255,0.50)' }}>{f}</Text>
                        </HStack>
                    ))}
                </Box>
            </Box>
        </Flex>

        {/* ── Painel direito: Formulário ── */}
        <Flex
            flex={{ base: 1, lg: '0 0 480px' }}
            direction="column"
            align="center"
            justify="center"
            px={{ base: 8, md: 14 }}
            py={12}
            bg="white"
            _dark={{ bg: "#111111", borderLeftColor: "#2A2A2A" }}
            borderLeftWidth="1px"
            borderLeftColor="gray.100"
            position="relative"
        >
            {/* Accent top stripe */}
            <Box
                position="absolute"
                top={0} left={0} right={0}
                h="3px"
                style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(251,146,60,0.7) 50%, transparent 100%)' }}
            />
            <Stack gap={6} w="100%" maxW="380px">

                {/* Logo para mobile */}
                <HStack hideFrom="lg" gap={3} justify="center">
                    <Image src="/icon.png" h="36px" />
                    <Box>
                        <Text fontWeight="bold" fontSize="lg">Bonífica</Text>
                        <Text fontSize="xs" color="fg.muted">Escritório de Núcleos</Text>
                    </Box>
                </HStack>

                <Box>
                    <Text fontSize="2xl" fontWeight="bold" letterSpacing="-0.02em">Bem-vindo(a) de volta</Text>
                    <Text color="fg.muted" fontSize="sm" mt={1}>Faça login para acessar seu núcleo</Text>
                </Box>

                <Form.Container onSubmit={handleSubmit}>
                    <Form.Text
                        required
                        type="email"
                        label="Endereço de e-mail"
                        placeholder="maria@bonificaoficial.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={loading}
                        inputmode="email"
                        size="lg"
                    />
                    <Form.Password
                        required
                        label="Senha"
                        placeholder="••••••••"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={loading}
                        size="lg"
                    />
                    <Button
                        type="submit"
                        w="100%"
                        size="lg"
                        colorPalette="orange"
                        loading={loading}
                        loadingText="Autenticando..."
                    >
                        Entrar no núcleo
                    </Button>
                </Form.Container>

                <HStack gap={3}>
                    <Separator flex="1" />
                    <Text fontSize="xs" color="fg.subtle">ou</Text>
                    <Separator flex="1" />
                </HStack>

                <Box textAlign="center">
                    <Text fontSize="sm" color="fg.muted">Esqueceu a senha?</Text>
                    <Link to="/recuperacao">
                        <Text fontSize="sm" fontWeight="semibold" color="orange.500">
                            <UI.Icon name="key" /> Recuperar acesso
                        </Text>
                    </Link>
                </Box>

            </Stack>
        </Flex>

    </Flex>
    )
}
